import { Link } from 'react-router-dom';
import { inAppPath } from '@/lib/inAppPath';

export interface ChannelRefLinkProps {
  /** The channel's display name, rendered after the `#`. */
  label: string;
  /**
   * The permalink stored on the mention node. Absolute — the API mints it from
   * `WEB_BASE_URL` so the same string works when pasted outside the app.
   */
  href?: string | undefined;
  className?: string;
}

/**
 * A `#channel` cross-reference inside a rendered message.
 *
 * WHY THIS IS NOT JUST AN `<a>`: the href is an ABSOLUTE url. A plain anchor
 * therefore performs a full document navigation — the SPA tears down and
 * remounts, the React Query cache is thrown away, every timeline refetches and
 * the reader loses their scroll position. Following a reference to a channel in
 * the workspace you are already looking at should cost a route change, so a
 * same-origin permalink is routed through React Router and only a genuinely
 * foreign URL falls back to an anchor.
 *
 * `inAppPath` is the one rule for "is this link a place inside the app" (posts,
 * chat messages and cards draw their links with it too). It answers null for
 * another origin - a message forwarded from another workspace, or a deployment
 * whose WEB_BASE_URL has since changed - and those stay real outbound links.
 */
export function ChannelRefLink({ label, href, className }: ChannelRefLinkProps) {
  const path = href === undefined || href === '' ? null : inAppPath(href);

  if (path !== null) {
    return (
      <Link to={path} className={className}>
        #{label}
      </Link>
    );
  }

  if (href !== undefined && href !== '') {
    return (
      <a href={href} target="_blank" rel="noreferrer noopener" className={className}>
        #{label}
      </a>
    );
  }

  // Unresolved reference: the composer never managed to look the channel up, or
  // the reader cannot see it. It still reads correctly as `#name`.
  return <span className={className}>#{label}</span>;
}
